import i18n from '@/i18n';
import { getDateFromFullDate, isValidDateTime, isDateRangeYear } from '@/utils/format';
import {
    regexEmail,
    regexUrl,
    regexKatakana,
    regexNumberWithHyphens,
    regexPassword,
    regexSubdomain,
    regexHalfWidthAlphanumeric,
    regexFullWidthCharacters,
    regexHiragana,
    regexFullWidthKatakana,
    regexHalfWidthKatakana,
    regexDynamicRange,
    regexNumberIntDecimal,
    regexSerialCode
} from '@/utils/regex';
import { validateNumberRange } from '@/utils/handlers';

function expandEachConfig(form, configForm) {
    Object.keys(configForm).forEach((key) => {
        const valueField = form[key];
        const configField = configForm[key];

        // Nếu là array và có $each
        if (Array.isArray(valueField) && configField.$each) {
            // Nếu $each đã là array (đã được sync) thì không cần expand lại
            if (Array.isArray(configField.$each)) return;
            // Lưu template gốc trước khi expand
            configField._$eachTemplate = JSON.parse(JSON.stringify(configField.$each));
            // clone config mẫu cho từng phần tử
            const template = configField.$each;
            configField.$each = valueField.map((item) => {
                const cloned = JSON.parse(JSON.stringify(template));
                // Nếu phần tử là primitive thì return config trực tiếp
                if (typeof item !== 'object' || item === null) {
                    return cloned;
                }
                // Nếu là object thì đi sâu
                expandEachConfig(item, cloned);
                return cloned;
            });
        } else if (typeof valueField === 'object' && valueField !== null) {
            expandEachConfig(valueField, configField);
        }
    });
}

function validateAllField(form, configForm, isValid = true) {
    expandEachConfig(form, configForm);
    Object.keys(configForm).forEach((key) => {
        const valueField = form[key];
        const configField = configForm[key];
        // case array
        if (Array.isArray(valueField)) {
            if (!configField?.$each && !Array.isArray(configField.$each)) {
                isValid = validateField(valueField, configField, isValid) && isValid;
            } else {
                valueField.forEach((item, idx) => {
                    if (typeof item === 'object' && item !== null) {
                        isValid = validateAllField(item, configField.$each[idx], isValid) && isValid;
                    } else {
                        isValid = validateField(item, configField.$each[idx], isValid) && isValid;
                    }
                });
            }
        }
        // case object
        else if (typeof valueField === 'object' && valueField !== null) {
            if (configField?.typesValidate) {
                isValid = validateField(valueField, configField, isValid) && isValid;
            } else {
                isValid = validateAllField(valueField, configField, isValid) && isValid;
            }
        }
        // case primitive
        else {
            isValid = validateField(valueField, configField, isValid) && isValid;
        }
    });
    return isValid;
}

function validateField(fieldValue, fieldConfig, isValid = true) {
    let valueTrim = fieldValue || fieldValue === 0 ? String(fieldValue).trim() : null;
    let lengthValueTrim = valueTrim?.length;
    fieldConfig.error = false;
    fieldConfig.errorCustomMess = '';
    fieldConfig.errorText = '';

    if (fieldConfig?.typesValidate?.includes('required')) {
        if (!valueTrim) {
            fieldConfig.isRequired = fieldConfig?.messageCustom ? false : true;
            fieldConfig.error = true;
            isValid = false;
        }
    }
    if (fieldConfig?.typesValidate?.includes('requiredCustomMsg')) {
        if (!valueTrim) {
            fieldConfig.errorCustomMess = i18n.t('validateField.required', {
                field: fieldConfig.errorField
            });
            fieldConfig.error = true;
            isValid = false;
        }
    }

    if (fieldConfig?.typesValidate?.includes('maxlength_textarea') && valueTrim && fieldConfig?.length && lengthValueTrim > fieldConfig.length) {
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('maxCustom') && valueTrim && fieldConfig?.length && lengthValueTrim > fieldConfig.length) {
        fieldConfig.errorCustomMess = i18n.t('validateField.maxCustom', {
            field: fieldConfig.errorField,
            length: fieldConfig.length
        });
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('max10') && valueTrim && lengthValueTrim > 10) {
        fieldConfig.errorCustomMess = i18n.t('validateField.max10', {
            field: fieldConfig.errorField
        });
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('max20') && valueTrim && lengthValueTrim > 20) {
        fieldConfig.errorCustomMess = i18n.t('validateField.max20', {
            field: fieldConfig.errorField
        });
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('max100') && valueTrim && lengthValueTrim > 100) {
        fieldConfig.errorCustomMess = i18n.t('validateField.max100', {
            field: fieldConfig.errorField
        });
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('max255') && valueTrim && lengthValueTrim > 255) {
        fieldConfig.errorCustomMess = fieldConfig?.messageCustom
            ? ''
            : i18n.t('validateField.max255', {
                  field: fieldConfig.errorField
              });
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('max500') && valueTrim && lengthValueTrim > 500) {
        fieldConfig.errorCustomMess = i18n.t('validateField.max500', {
            field: fieldConfig.errorField
        });
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('number-hyphens') && valueTrim && lengthValueTrim <= 20 && !regexNumberWithHyphens(valueTrim)) {
        fieldConfig.errorCustomMess = i18n.t('validateField.incorrectFormat', {
            field: fieldConfig.errorField
        });
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('sub-domain') && valueTrim && lengthValueTrim <= 255 && !regexSubdomain(valueTrim)) {
        fieldConfig.errorCustomMess = i18n.t('validateField.incorrectFormat', {
            field: fieldConfig.errorField
        });
        fieldConfig.error = true;
        isValid = false;
    }

    if (
        fieldConfig?.typesValidate?.includes('half-width_alphanumeric_characters') &&
        valueTrim &&
        lengthValueTrim <= 255 &&
        !regexHalfWidthAlphanumeric(valueTrim)
    ) {
        fieldConfig.error = true;
        isValid = false;
    }

    if (
        fieldConfig?.typesValidate?.includes('full-width_characters') &&
        valueTrim &&
        lengthValueTrim <= 255 &&
        !regexFullWidthCharacters(valueTrim)
    ) {
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('hiragana') && valueTrim && lengthValueTrim <= 255 && !regexHiragana(valueTrim)) {
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('full-width_katakana') && valueTrim && lengthValueTrim <= 255 && !regexFullWidthKatakana(valueTrim)) {
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('half-width_katakana') && valueTrim && lengthValueTrim <= 255 && !regexHalfWidthKatakana(valueTrim)) {
        fieldConfig.error = true;
        isValid = false;
    }

    if (
        fieldConfig?.typesValidate?.includes('format_numerical_value') &&
        valueTrim &&
        lengthValueTrim <= 255 &&
        !regexDynamicRange(valueTrim, '', '')
    ) {
        fieldConfig.error = true;
        isValid = false;
    }

    if (
        fieldConfig?.typesValidate?.includes('range_numerical') &&
        valueTrim &&
        lengthValueTrim <= 255 &&
        !regexDynamicRange(valueTrim, fieldConfig?.from_range, fieldConfig?.to_range)
    ) {
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('password') && valueTrim && lengthValueTrim <= 255 && !regexPassword(valueTrim)) {
        fieldConfig.isPassword = true;
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('email') && valueTrim && lengthValueTrim <= 255 && !regexEmail(valueTrim)) {
        fieldConfig.isEmail = fieldConfig?.messageCustom ? false : true;
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('url') && valueTrim && lengthValueTrim <= 255 && !regexUrl(valueTrim)) {
        fieldConfig.isUrl = true;
        fieldConfig.error = true;
        isValid = false;
    }

    if (fieldConfig?.typesValidate?.includes('katakana') && valueTrim && lengthValueTrim <= 255 && !regexKatakana(valueTrim)) {
        fieldConfig.isKatakana = fieldConfig?.messageCustom ? false : true;
        fieldConfig.error = true;
        isValid = false;
    }

    if (
        fieldConfig?.typesValidate?.includes('limit-input') &&
        valueTrim &&
        lengthValueTrim <= 255 &&
        fieldConfig?.isNumber &&
        fieldConfig?.min?.toString() &&
        fieldConfig?.max?.toString()
    ) {
        valueTrim = parseInt(valueTrim, 10);
        if (valueTrim < fieldConfig.min || valueTrim > fieldConfig.max || isNaN(valueTrim)) {
            fieldConfig.errorCustomMess = fieldConfig?.messageCustom
                ? ''
                : i18n.t('validateField.limitInput', {
                      field: fieldConfig.errorField,
                      min: fieldConfig.min,
                      max: fieldConfig.max
                  });
            fieldConfig.error = true;
            isValid = false;
        }
    }
    if (
        fieldConfig?.typesValidate?.includes('limit-character') &&
        valueTrim &&
        lengthValueTrim <= 255 &&
        fieldConfig?.min?.toString() &&
        fieldConfig?.max?.toString()
    ) {
        if (lengthValueTrim < fieldConfig.min || lengthValueTrim > fieldConfig.max) {
            fieldConfig.error = true;
            isValid = false;
        }
    }

    if (
        fieldConfig?.typesValidate?.includes('integerMaxMin') &&
        valueTrim &&
        lengthValueTrim <= 255 &&
        fieldConfig?.min?.toString() &&
        fieldConfig?.max?.toString()
    ) {
        const regex = /^-?\d{1,10}$/;
        if (!regex.test(valueTrim)) {
            fieldConfig.error = true;
            isValid = false;
            return false;
        }
        const number = parseInt(valueTrim, 10);
        let checkRegex = number >= fieldConfig.min && number <= fieldConfig.max;
        if (!checkRegex) {
            fieldConfig.error = true;
            isValid = false;
        }
    }

    if (
        fieldConfig?.typesValidate?.includes('integerDecimal') &&
        valueTrim &&
        lengthValueTrim <= 255 &&
        !regexNumberIntDecimal(valueTrim, fieldConfig?.integer, fieldConfig?.decimal)
    ) {
        fieldConfig.error = true;
        isValid = false;
    }

    // date validation
    if (fieldConfig?.typesValidate?.includes('requiredDate')) {
        if (!valueTrim) {
            fieldConfig.error = true;
            fieldConfig.errorText = i18n.t('validateField.requiredDate', {
                field: fieldConfig.errorField
            });
            isValid = false;
        }
    }

    if (fieldConfig?.typesValidate?.includes('date')) {
        if (valueTrim) {
            if (!getDateFromFullDate(valueTrim)) {
                fieldConfig.error = true;
                fieldConfig.errorText = i18n.t('validateField.incorrectFormat', {
                    field: fieldConfig.errorField
                });
                isValid = false;
            }
        }
    }
    if (fieldConfig?.typesValidate?.includes('date_year_range')) {
        if (valueTrim && getDateFromFullDate(valueTrim) && fieldConfig.minYear) {
            if (!isDateRangeYear(valueTrim, fieldConfig.minYear, fieldConfig.maxYear)) {
                fieldConfig.error = true;
                fieldConfig.errorText = i18n.t('validateField.dateMinMaxYear', {
                    minDateYear: fieldConfig?.minDateYear ? fieldConfig?.minDateYear : `1800/01/01`,
                    maxDateYear: fieldConfig?.maxDateYear ? fieldConfig?.maxDateYear : `2100/12/31`
                });
                isValid = false;
            }
        }
    }

    if (fieldConfig?.typesValidate?.includes('dateTime')) {
        if (valueTrim) {
            if (!isValidDateTime(valueTrim)) {
                fieldConfig.error = true;
                fieldConfig.errorText = i18n.t('validateField.incorrectFormat', {
                    field: fieldConfig.errorField
                });
                isValid = false;
            }
        }
    }

    if (fieldConfig?.typesValidate?.includes('requiredSelect')) {
        if (!valueTrim) {
            fieldConfig.error = true;
            fieldConfig.isRequired = fieldConfig?.messageCustom ? false : true;
            isValid = false;
        }
    }
    if (fieldConfig?.typesValidate?.includes('serialCode')) {
        if (valueTrim && !regexSerialCode(valueTrim)) {
            fieldConfig.errorCustomMess = '';
            fieldConfig.error = true;
            fieldConfig.errorText = i18n.t('validateField.incorrectFormat', {
                field: fieldConfig.errorField
            });
            isValid = false;
        }
    }

    if (fieldConfig?.typesValidate?.includes('number-range')) {
        if (valueTrim && lengthValueTrim <= 255 && !validateNumberRange(valueTrim, fieldConfig?.min, fieldConfig?.max)) {
            fieldConfig.error = true;
            fieldConfig.errorCustomMess = i18n.t('validateField.limitInput', {
                field: fieldConfig.errorField,
                min: fieldConfig?.min,
                max: fieldConfig?.max
            });
            isValid = false;
        }
    }

    if (fieldConfig?.typesValidate?.includes('minimum')) {
        if (valueTrim && lengthValueTrim <= 255 && !validateNumberRange(valueTrim, fieldConfig?.min, fieldConfig?.max, 'min')) {
            fieldConfig.error = true;
            fieldConfig.errorCustomMess = i18n.t('validateField.limitMin', {
                field: fieldConfig.errorField,
                min: fieldConfig?.min
            });
            isValid = false;
        }
    }

    return isValid;
}

function resetConfigError(config) {
    if (config && typeof config === 'object') {
        if ('error' in config) config.error = false;
        if ('errorCustomMess' in config) config.errorCustomMess = '';
        if ('errorText' in config) config.errorText = '';
        if ('isRequired' in config) config.isRequired = false;
        Object.keys(config).forEach((key) => {
            if (typeof config[key] === 'object' && config[key] !== null && key !== '$each') {
                resetConfigError(config[key]);
            }
        });
    }
}

function syncAddEachConfig(configField, formArray, index = -1) {
    if (!configField?.$each) return;
    const template = Array.isArray(configField.$each) ? configField.$each[0] || configField._$eachTemplate : configField.$each;
    if (!template) return;
    if (!Array.isArray(configField.$each)) {
        // Lần đầu: tạo config cho TẤT CẢ phần tử hiện có trong formArray
        configField.$each = formArray.map(() => {
            const cloned = JSON.parse(JSON.stringify(template));
            resetConfigError(cloned);
            return cloned;
        });
    } else {
        // Đã expand: chỉ thêm 1 config tại vị trí tương ứng
        const cloned = JSON.parse(JSON.stringify(template));
        resetConfigError(cloned);
        if (index === -1 || index >= configField.$each.length) {
            configField.$each.push(cloned);
        } else {
            configField.$each.splice(index, 0, cloned);
        }
    }
}

function syncRemoveEachConfig(configField, index) {
    if (!configField?.$each || !Array.isArray(configField.$each)) return;
    configField.$each.splice(index, 1);
}

// Demo
// const form = {
//     name: '42',
//     email: 'rewr@gmail.com',
//     option_age: [1],
//     string_arr: ['1'],
//     object_arr: [
//         {
//             name: '3242',
//             age: '23'
//         },
//         {
//             name: '4433',
//             age: 25
//         }
//     ],
//     type_id: null
// };

// const configForm = {
//     name: {
//         error: false,
//         errorField: i18n.t('client.name'),
//         errorCustomMess: '',
//         typesValidate: ['required', 'max255']
//     },
//     email: {
//         error: false,
//         errorField: i18n.t('clientSupport.email'),
//         errorCustomMess: '',
//         typesValidate: ['email', 'max255']
//     },
//     option_age: {
//         error: false,
//         errorField: i18n.t('clientSupport.age'),
//         errorCustomMess: '',
//         typesValidate: ['required']
//     },
//     object_arr: {
//         $each: {
//             name: {
//                 error: false,
//                 errorField: i18n.t('clientSupport.age'),
//                 errorCustomMess: '',
//                 typesValidate: ['required']
//             },
//             age: {
//                 error: false,
//                 errorField: i18n.t('clientSupport.age'),
//                 errorCustomMess: '',
//                 typesValidate: ['required']
//             }
//         }
//     },
//     string_arr: {
//         $each: {
//             error: false,
//             errorField: i18n.t('clientSupport.age'),
//             errorCustomMess: '',
//             typesValidate: ['required']
//         }
//     },
//     type_id: {
//         trackBy: 'id',
//         label: 'name',
//         errorField: '種別',
//         valueSync: null,
//         error: false,
//         isRemove: false,
//         isRequired: false,
//         typesValidate: ['requiredSelect'],
//         openDirection: 'top'
//     }
// };

export { expandEachConfig, validateField, validateAllField, syncAddEachConfig, syncRemoveEachConfig };
